/* eslint-disable */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import MemberAvatar from "../components/MemberAvatar";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { toast, Toaster } from "sonner";

const POLL_MS = 15000;

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function MessageThread() {
  const { threadId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const lastMsgTsRef = useRef(null);

  const scrollToBottom = () => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  };

  // Initial load + polling
  useEffect(() => {
    let cancelled = false;
    let timer;
    const load = async (isInitial) => {
      try {
        const params = !isInitial && lastMsgTsRef.current ? { after: lastMsgTsRef.current } : {};
        const r = await api.get(`/community/messages/threads/${threadId}`, { params });
        if (cancelled) return;
        setThread(r.data.thread);
        const newMsgs = r.data.messages || [];
        if (isInitial) {
          setMessages(newMsgs);
        } else if (newMsgs.length > 0) {
          setMessages((prev) => [...prev, ...newMsgs]);
        }
        if (newMsgs.length > 0) {
          lastMsgTsRef.current = newMsgs[newMsgs.length - 1].created_at;
          setTimeout(scrollToBottom, 50);
        }
        // Mark unread messages read (fire-and-forget)
        if (r.data.thread?.unread_count > 0) {
          api.post(`/community/messages/threads/${threadId}/read`).catch(() => {});
        }
      } catch (e) {
        if (isInitial) {
          toast.error(e?.response?.data?.detail || "Thread not found");
          nav("/community/messages");
        }
      } finally {
        if (isInitial && !cancelled) setLoading(false);
      }
    };
    load(true);
    timer = setInterval(() => load(false), POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [threadId, nav]);

  const send = async () => {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    // Optimistic append
    const tempId = `local_${Date.now()}`;
    const optimistic = {
      message_id: tempId, thread_id: threadId,
      sender_id: user.user_id, body, created_at: new Date().toISOString(),
      read_by: [user.user_id], _pending: true,
    };
    setMessages((prev) => [...prev, optimistic]);
    setDraft("");
    setTimeout(scrollToBottom, 30);
    try {
      const r = await api.post(`/community/messages/threads/${threadId}`, { body });
      lastMsgTsRef.current = r.data.created_at;
      setMessages((prev) => prev.map(m => m.message_id === tempId ? r.data : m));
    } catch (e) {
      // Rollback optimistic message
      setMessages((prev) => prev.filter(m => m.message_id !== tempId));
      setDraft(body);
      toast.error(e?.response?.data?.detail || "Message failed to send");
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <AppShell>
      <Toaster position="top-right" richColors />
      <section className="m-container" style={{ maxWidth: 720, display: "flex", flexDirection: "column", height: "calc(100vh - 120px)" }}
        data-testid="message-thread">
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 0", borderBottom: "1px solid var(--m-border)" }}>
          <Link to="/community/messages" className="m-btn-ghost" style={{ padding: "6px 12px", fontSize: 12 }}>← Inbox</Link>
          {thread?.other && (
            <>
              <MemberAvatar user={thread.other} size={40} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700 }} data-testid="thread-other-name">
                  <Link to={`/community/members/${thread.other.user_id}`}
                    style={{ color: "inherit", textDecoration: "none" }}>
                    {thread.other.display_name || thread.other.name}
                  </Link>
                </div>
                {thread.other.city && (
                  <div className="m-muted small">{thread.other.city}</div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Messages */}
        <div ref={scrollRef} data-testid="thread-messages"
          style={{ flex: 1, overflowY: "auto", padding: "18px 4px", display: "flex", flexDirection: "column", gap: 8 }}>
          {loading && <div className="m-loading-inline">Loading…</div>}
          {!loading && messages.length === 0 && (
            <div style={{ textAlign: "center", opacity: 0.5, padding: "40px 20px" }}>
              <p className="m-muted">No messages yet. Say hi 👋</p>
            </div>
          )}
          {messages.map((m) => {
            const mine = m.sender_id === user?.user_id;
            return (
              <div key={m.message_id}
                data-testid={`msg-${m.message_id}`}
                style={{
                  display: "flex",
                  justifyContent: mine ? "flex-end" : "flex-start",
                  gap: 8,
                  opacity: m._pending ? 0.6 : 1,
                }}>
                <div style={{
                  maxWidth: "72%",
                  padding: "10px 14px",
                  borderRadius: mine ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                  background: mine ? "var(--gold)" : "#fff",
                  color: mine ? "#fff" : "var(--m-text)",
                  border: mine ? "none" : "1px solid var(--m-border)",
                  fontSize: 14, lineHeight: 1.5,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}>
                  {m.body}
                  <div style={{
                    fontSize: 10, marginTop: 4,
                    opacity: mine ? 0.85 : 0.55,
                    textAlign: mine ? "right" : "left",
                  }}>{fmtTime(m.created_at)}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Composer */}
        <div style={{ display: "flex", gap: 10, padding: "14px 0 8px", borderTop: "1px solid var(--m-border)" }}>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Type a message…"
            rows={2}
            data-testid="thread-composer"
            style={{
              flex: 1, padding: "10px 14px", fontSize: 14,
              border: "1px solid var(--m-border)", borderRadius: 10,
              resize: "none", fontFamily: "inherit",
              background: "var(--cream)",
            }}
            disabled={sending}
          />
          <button
            className="m-btn-gold"
            onClick={send}
            disabled={!draft.trim() || sending}
            data-testid="thread-send"
            style={{ alignSelf: "stretch", padding: "0 22px" }}
          >{sending ? "…" : "Send"}</button>
        </div>
      </section>
    </AppShell>
  );
}
